import { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useTeamStore } from '../../lib/store/teamStore';
import { useAuthentication } from '../../hooks/useAuthentication';
import Card from '../common/Card';
import Button from '../common/Button';
import Select from '../common/Select';
import Alert from '../common/Alert';
import Badge from '../common/Badge';
import { Dialog } from '../common/Dialog';
import { TeamMemberWithUserInfo } from '../../types';
import { ShiftTypeSelector } from './ShiftTypeSelector';
import { ShiftTypeBadge } from './ShiftTypeBadge';

export const TeamMembersList = () => {
  const { teamId } = useParams<{ teamId: string }>();
  const { user } = useAuthentication();
  const { 
    teamMembers, 
    isLoading, 
    error, 
    fetchTeamMembers, 
    updateTeamMember, 
    removeTeamMember 
  } = useTeamStore();
  
  // State for edit modal
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editingMember, setEditingMember] = useState<TeamMemberWithUserInfo | null>(null);
  const [editRole, setEditRole] = useState<'admin' | 'provider'>('provider');
  const [editShiftType, setEditShiftType] = useState<'days_only' | 'nights_only' | 'both'>('both');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  
  // State for remove modal
  const [isRemoveOpen, setIsRemoveOpen] = useState(false);
  const [removingMember, setRemovingMember] = useState<TeamMemberWithUserInfo | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  
  // Fetch all team members directly with service role if needed
  const fetchAllTeamMembers = useCallback(async () => {
    if (!teamId) return;
    
    try {
      // First try the regular fetch through the store
      await fetchTeamMembers(teamId);
    } catch (error) {
      console.error('Error fetching team members:', error);
    }
  }, [teamId, fetchTeamMembers]);
  
  useEffect(() => {
    if (teamId) {
      fetchAllTeamMembers();
    }
  }, [teamId, fetchAllTeamMembers]);
  
  // Check if current user is admin of the team
  const isAdmin = teamMembers.some(
    member => member.user_id === user?.id && member.role === 'admin'
  );
  
  // Handler for opening edit modal
  const handleEditClick = (member: TeamMemberWithUserInfo) => {
    setEditingMember(member);
    setEditRole(member.role);
    setEditShiftType(member.shift_type);
    setActionError(null);
    setIsEditOpen(true);
  };
  
  // Handler for saving member changes
  const handleSaveMember = async () => {
    if (!teamId || !editingMember) return;
    
    setIsSubmitting(true);
    setActionError(null);
    
    try {
      await updateTeamMember({
        team_id: teamId,
        user_id: editingMember.user_id,
        role: editRole,
        shift_type: editShiftType
      });
      
      setIsEditOpen(false);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to update team member. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Handler for opening remove modal
  const handleRemoveClick = (member: TeamMemberWithUserInfo) => {
    setRemovingMember(member);
    setIsRemoveOpen(true);
  };
  
  // Handler for confirming member removal
  const handleConfirmRemove = async () => {
    if (!teamId || !removingMember) return;
    
    setIsRemoving(true);
    
    try {
      await removeTeamMember(teamId, removingMember.user_id);
      setIsRemoveOpen(false);
    } catch (err) {
      console.error('Failed to remove team member:', err);
    } finally {
      setIsRemoving(false);
    }
  };
  
  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin h-8 w-8 border-t-2 border-b-2 border-primary rounded-full"></div>
      </div>
    );
  }
  
  if (error) {
    return (
      <Alert variant="error" className="mb-4">
        {error}
      </Alert>
    );
  }
  
  if (teamMembers.length === 0) {
    return (
      <Card className="p-6">
        <div className="text-center py-6 text-gray-500">
          No team members found. Invite members to get started.
        </div>
      </Card>
    );
  }
  
  return (
    <div>
      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4">Team Members</h2>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Shift Type
                </th>
                {isAdmin && (
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {teamMembers.map(member => (
                <tr key={member.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="font-medium text-gray-900">
                      {member.user?.full_name || 'Unknown User'}
                      {member.user_id === user?.id && (
                        <span className="text-xs text-gray-500 ml-2">(You)</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                    {member.user?.email || 'No email'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Badge variant={member.role === 'admin' ? 'primary' : 'secondary'}>
                      {member.role === 'admin' ? 'Admin' : 'Provider'}
                    </Badge>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <ShiftTypeBadge type={member.shift_type} />
                  </td>
                  {isAdmin && (
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <Button
                        variant="text"
                        onClick={() => handleEditClick(member)}
                        className="text-primary hover:text-primary-dark mr-4"
                      >
                        Edit
                      </Button>
                      {/* Don't allow removing yourself */}
                      {member.user_id !== user?.id && (
                        <Button
                          variant="text"
                          onClick={() => handleRemoveClick(member)}
                          className="text-red-600 hover:text-red-800"
                        >
                          Remove
                        </Button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      
      {/* Edit Member Dialog */}
      <Dialog
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        title="Edit Team Member"
      >
        {actionError && (
          <Alert variant="error" className="mb-4">
            {actionError}
          </Alert>
        )}
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name
            </label>
            <div className="py-2 px-3 bg-gray-100 rounded-md">
              {editingMember?.user?.full_name || 'Unknown User'}
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <div className="py-2 px-3 bg-gray-100 rounded-md">
              {editingMember?.user?.email || 'No email'}
            </div>
          </div>
          
          <Select
            label="Role"
            value={editRole}
            onChange={e => setEditRole(e.target.value as 'admin' | 'provider')}
            required
            options={[
              { value: 'provider', label: 'Provider' },
              { value: 'admin', label: 'Administrator' }
            ]}
          />
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Shift Type
            </label>
            <ShiftTypeSelector
              value={editShiftType}
              onChange={setEditShiftType}
              disabled={isSubmitting}
            />
          </div>
        </div>
        
        <div className="mt-6 flex justify-end space-x-4">
          <Button
            variant="outline"
            onClick={() => setIsEditOpen(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveMember}
            isLoading={isSubmitting}
          >
            Save Changes
          </Button>
        </div>
      </Dialog>
      
      {/* Remove Member Dialog */}
      <Dialog
        isOpen={isRemoveOpen}
        onClose={() => setIsRemoveOpen(false)}
        title="Remove Team Member"
      >
        <div className="text-center">
          <div className="text-xl">⚠️</div>
          <p className="mt-2 text-gray-600">
            Are you sure you want to remove <span className="font-semibold">{removingMember?.user?.full_name || 'Unknown User'}</span> from this team?
          </p>
          <p className="mt-1 text-gray-500 text-sm">
            This action cannot be undone.
          </p>
        </div>
        
        <div className="mt-6 flex justify-end space-x-4">
          <Button
            variant="outline"
            onClick={() => setIsRemoveOpen(false)}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirmRemove}
            isLoading={isRemoving}
          >
            Remove Member
          </Button>
        </div>
      </Dialog>
    </div>
  );
}; 